import React, { useEffect, useRef, useState } from 'react'
import "dropdown-menu-pattern/dist/main.css"
 
interface DropdownOption {
  id: string | number;
  value: string;
}
 
interface DropdownProps {
  label: string;
  selectedValue: string;
  options: (string | DropdownOption)[];
  onSelect: (value: string) => void;
  className?: string;
  placeholder?: string;
  fieldSelect?: boolean;
  btnClassName?: string;
}
 
const Dropdown: React.FC<DropdownProps> = ({
  label,
  selectedValue,
  options,
  onSelect,
  className = '',
  fieldSelect = false,
  placeholder = 'Choose option',
  btnClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside the dropdown or pressing Escape
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!isOpen) return;
      const el = dropdownRef.current;
      if (el && event.target && !el.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);
 
  if (fieldSelect) {
    return (
      <div
        className={`ds-dropdown ${className}`}
        data-ds-theme="vessel"
        data-ds-variant="basic"
        data-ds-version="2"
        data-list-type="option"
        data-menu-type="standard-versioned"
        data-select-label="selected"
      >
        <div className="ds-dropdown__drop-menu">
          <label className="ds-dropdown__label">
            <span className="ds-dropdown__label-text">{label}</span>
          </label>
 
          <div className="ds-dropdown__native-wrap" style={{ maxWidth: '100%' }}>
            <select
              className="ds-dropdown__native-select"
              value={selectedValue}
              onChange={(e) => onSelect(e.target.value)}
              aria-label={label}
            >
              <option value="" disabled hidden>
                {placeholder}
              </option>
 
              {(Array.isArray(options) ? options : []).map((option, index) => {
                const key = typeof option === 'object' ? option.id || index : option;
                const value = typeof option === 'object' ? option.value : option;
                return (
                  <option key={key} value={value}>
                    {value}
                  </option>
                );
              })}
            </select>
 
            <img
              className="ds-dropdown__chevron"
              src="/assets/images/chevrondown.svg"
              alt=""
              width={24}
              height={24}
            />
          </div>
        </div>
      </div>
    );
  }
 
  const toggleDropdown = () => setIsOpen(!isOpen);
 
  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };
 
  return (
    <div
      ref={dropdownRef}
      className={`ds-dropdown ${className}`}
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
      data-list-type="option"
      data-menu-type="standard-versioned"
      data-select-label="selected"
    >
      <div className="ds-dropdown__drop-menu">
        <label className="ds-dropdown__label">
          <span className="ds-dropdown__label-text">{label}</span>
        </label>
 
        <button
          className={`ds-dropdown__trigger-button ${btnClassName}`}
          onClick={toggleDropdown}
          type="button"
          aria-expanded={isOpen}
          style={{ padding: '0.5rem 0.75rem' }}
        >
          <span className="ds-dropdown__trigger-text" style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedValue || placeholder}</span>
          <img className="ds-dropdown__chevron" src="/assets/images/chevrondown.svg" alt="" width={24} height={24} />
        </button>
 
        <ul
          className="ds-dropdown__menu-list"
          style={{ display: isOpen ? 'block' : 'none', minWidth: 200, maxWidth: 360, boxSizing: 'border-box' }}
        >
          {(Array.isArray(options) ? options : []).map((option, index) => {
            const key = typeof option === 'object' ? option.id || index : option;
            const value = typeof option === 'object' ? option.value : option;
            const isSelected = selectedValue === value;
            return (
              <li key={key} style={{ padding: 0, margin: 0 }}>
                <button
                  type="button"
                  className={`drop-menu-list-op ${isSelected ? 'active' : ''}`}
                  onClick={() => handleSelect(value)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '5px', justifyContent: 'flex-start', flexDirection: 'row', textAlign: 'left', marginRight: 2, border: 'none', cursor: 'pointer', color: 'inherit'}}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F2F8FB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span style={{ flex: '0 0 16px', order: 1, minWidth: 16 }}>
                    {isSelected ? (
                      <img
                        className="drop-menu-button-img"
                        src="/assets/images/checkmarkIcon.svg"
                        alt="selected"
                        width={16}
                        height={16}
                      />
                    ) : (
                      <span style={{ width: 16, height: 16, display: 'inline-block' }} />
                    )}
                  </span>
 
                  <span className="drop-menu-list-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, order: 2, textAlign: 'left' }}>{value}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
 
export default Dropdown;